// GitLab WebUI issue #31 / M001-D023 §8.8 (`/link/<key>` anonymous view-only viewer).
// Feature: M001-F0232 / M001-F0294 (both allocated in studioaeye-Core, design v0.8).
// Test: T-RES-SHARE-001 cases T/U; T-RES-PROJ-001 case K.
// No new WebUI Feature/Test ID is allocated by this file — the traceability table
// (M001-D023 §13.1) already names `studioaeye-web` as the implementing repo for the
// route/page (case T) and the page chrome (case U) under those existing IDs.
//
// Owner's verbatim scenario this satisfies: "someone that does not have an account
// ... I want to share a link that has an access key ... The link needs to be simple
// and pasteable into a WhatsApp group ... https://studioaeye.com/link/<key#######>
// ... The user must be able to view the choreo and not have any other blocks."
//
// Wire contract: the SAME unauthenticated `POST /api/v1/resource-sharing/link-
// projections { token }` operation SharedChoreography.js already uses for the older
// fragment-carrying flow (§8.1, unchanged by §8.8) — `choreographyApi
// .getSharingLinkProjection`. §8.2/§11 non-disclosure: EVERY failure (malformed key,
// unresolvable, expired, revoked, or a resolved resource with no published
// `safeProjection`) returns the identical opaque 404 from Core, so this page renders
// one generic "not available" state for all of them — never a distinguishable reason.
//
// DECIDED SPLIT OF OWNERSHIP for `/link/:key` (owner ruling, tracked in
// studioaeye-Core#61, in flight): Core keeps ownership of the HTTP document at
// this path; WebUI owns the rendering. Core must keep the document because
// only a server can set `X-Robots-Tag`/`Referrer-Policy`/`Cache-Control:
// no-store` as real response headers, return the identical opaque 404 across
// malformed/unresolvable/expired/revoked, apply the token-digest rate limit,
// and serve the generic OG card to an unfurl fetch before any JS runs (all of
// which studioaeye-Core `src/routes/link-shortlink.routes.ts`, merged MR !433
// / issue #56, already does). A redirect and a Core-side parse of the SPA
// asset manifest were both considered and rejected (the existing Core test
// asserts no 301/302 at all; parsing the manifest is needless coupling).
// Instead, on a resolvable key Core serves the deployed SPA's `index.html`
// with those same headers applied, and the SPA then routes `/link/:key` to
// THIS component for the actual rendering. That handoff is the work tracked
// under #61; until it lands, this route is reachable in this repo's own dev
// server and CI but not yet via the production domain. Nothing in this file
// changes once #61 lands — it becomes reachable, not different.
//
// M001-F0294's OWN engine-side gap (issue #57 — `resolveBaseActions()` not
// updated for either adapter, so `withLinkAuthority()` 404ed every anonymous
// `choreo_sheet`/`choreo_media` request regardless of a valid safeProjection)
// is FIXED and merged: studioaeye-Core MR !435, merge commit `9f7cb1f2`,
// 2026-08-15, adds the same `(!resource.projected ||
// resource.safeProjectionView)` branch to both adapters' `resolveBaseActions`
// plus `resourceSharingPhase2.linkSafeProjectionEndToEnd.test.ts`, which
// drives the real `withLinkAuthority()` path end-to-end. (An earlier revision
// of this comment called #57 an open blocker — that was true of the GitLab
// issue's stale `opened` state, not of `main`'s content as of !435; corrected
// here rather than left standing.)
import React, { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { choreographyApi } from '../../services/api';
import { projectChoreoCanvas } from '../choreography/blueprintComposer';
import { ContinuousCanvasEditor, scoreFromSnapshot, sourceFromSnapshot } from '../choreography/MusicChoreography';
import '../choreography/MusicChoreography.css';

const responseData = (response) => response?.data?.data || response?.data || {};
const isObject = (value) => Boolean(value) && typeof value === 'object' && !Array.isArray(value);

// The one supported resourceType today (choreo issue #22/#24: media has no
// anonymous-safe delivery path yet, so a `choreo_media` or any other
// resourceType projection has nothing this page can safely render — it is
// refused identically to an invalid key rather than guessed at).
const SUPPORTED_RESOURCE_TYPE = 'choreo_sheet';

// Mirrors Core's own `unavailablePage()` copy (link-shortlink.routes.ts) so the
// two surfaces read as one product regardless of which of them a visitor lands
// on while the production gap above is open.
const UNAVAILABLE_TITLE = "This link isn't available";
const UNAVAILABLE_BODY = 'It may have been removed, or the link may no longer be valid.';

// choreographySheetSafeProjection.service.ts (choreo #22, MR !127) is the sole
// producer of this shape today: { title, track, publishedRevisionId,
// revisionNumber, publishedAt, snapshot: { schemaVersion: 1, sections, blocks,
// scenes, cues } }. Validated defensively (not merely trusted) because a
// projection this page cannot interpret must degrade to the same "not
// available" state, never a partially-rendered or crashing page.
function sheetFromProjection(projection) {
  if (!isObject(projection) || projection.resourceType !== SUPPORTED_RESOURCE_TYPE
    || typeof projection.resourceId !== 'string' || !projection.resourceId) return null;
  const fields = projection.fields;
  if (!isObject(fields) || typeof fields.title !== 'string' || !fields.title.trim()) return null;
  if (!isObject(fields.snapshot)) return null;
  return { resourceId: projection.resourceId, ...fields };
}

// Sets the on-page signals §8.8.3 asks for (noindex/no-referrer) for parity
// with Core's route and for any environment that does reach this component
// directly (local dev, or once the flagged production gap above closes).
// These are meta-tag equivalents, not HTTP response headers — a CSR page
// cannot set response headers for its own document. Restored on unmount so
// they never leak onto another page rendered in the same SPA session.
function useAnonymousDocumentHead(title) {
  useEffect(() => {
    const previousTitle = document.title;
    document.title = title;
    const meta = (name, content) => {
      let tag = document.querySelector(`meta[name="${name}"]`);
      const created = !tag;
      if (created) { tag = document.createElement('meta'); tag.setAttribute('name', name); document.head.appendChild(tag); }
      const previous = tag.getAttribute('content');
      tag.setAttribute('content', content);
      return () => { if (created) tag.remove(); else if (previous !== null) tag.setAttribute('content', previous); };
    };
    const restoreRobots = meta('robots', 'noindex, nofollow');
    const restoreReferrer = meta('referrer', 'no-referrer');
    return () => { document.title = previousTitle; restoreRobots(); restoreReferrer(); };
  }, [title]);
}

// §8.8.3: a fixed, non-identifying preview — never the Sheet's own title —
// because an unfurl fetch (WhatsApp/Slack) can reach this URL before anyone
// has tapped the link. In production this is served by Core's route (see the
// file header); kept here too so a direct/local hit is never worse.
function AnonymousLinkHead() {
  return <>
    <meta property="og:type" content="website" />
    <meta property="og:title" content="Studio AEye — shared choreography" />
    <meta property="og:description" content="Someone shared a choreography Sheet with you on Studio AEye." />
  </>;
}

function UnavailableView() {
  useAnonymousDocumentHead('Studio AEye — shared choreography');
  return <main className="mc-shared-page"><AnonymousLinkHead /><section className="mc-panel mc-shared-card">
    <span className="mc-kicker">Studio AEye</span>
    <h1>{UNAVAILABLE_TITLE}</h1>
    <p>{UNAVAILABLE_BODY}</p>
  </section></main>;
}

function LoadingView() {
  useAnonymousDocumentHead('Studio AEye — shared choreography');
  return <main className="mc-shared-page"><AnonymousLinkHead /><section className="mc-panel mc-shared-card"><p>Loading…</p></section></main>;
}

function SheetView({ sheet }) {
  useAnonymousDocumentHead(sheet.title);
  const sourceRef = useRef(null);
  const source = sourceFromSnapshot(sheet.snapshot);
  const projectedScore = projectChoreoCanvas(source);
  const score = projectedScore.sections.some(({ allocations }) => allocations.length > 0)
    ? projectedScore
    : scoreFromSnapshot(sheet.snapshot);
  return <main className="mc-shared-page"><AnonymousLinkHead /><section className="mc-panel mc-shared-card">
    <span className="mc-kicker">Studio AEye · shared choreography</span>
    <h1>{sheet.title}</h1>
    <p className="mc-muted">
      {sheet.track?.title}{sheet.track?.artist ? ` · ${sheet.track.artist}` : ''}
      {Number.isInteger(sheet.revisionNumber) ? ` · Revision ${sheet.revisionNumber}` : ''}
    </p>
    <div className="mc-community-canvas-layout mc-shared-community-layout">
      <div className="mc-blank-page mc-blank-page-canvas">
        {source
          ? <ContinuousCanvasEditor inputRef={sourceRef} value={source} score={score} onChange={() => {}} ariaLabel="Published choreography source" readOnly />
          : <p className="mc-empty">This published routine does not have song sections yet.</p>}
        {(sheet.snapshot.cues || []).length > 0 && <div className="mc-shared-cues">
          <h2>Cues</h2>
          {sheet.snapshot.cues.map((cue) => <p key={cue.id}><strong>Count {cue.atCount}</strong> · {cue.text}</p>)}
        </div>}
      </div>
    </div>
  </section></main>;
}

export default function LinkViewer() {
  const { key } = useParams();
  const [state, setState] = useState({ status: 'loading', sheet: null });
  const requestedKeyRef = useRef(null);

  useEffect(() => {
    if (requestedKeyRef.current === key) return undefined;
    requestedKeyRef.current = key;
    let active = true;
    setState({ status: 'loading', sheet: null });
    choreographyApi.getSharingLinkProjection(key)
      .then((response) => {
        if (!active) return;
        const sheet = sheetFromProjection(responseData(response));
        setState(sheet ? { status: 'ready', sheet } : { status: 'unavailable', sheet: null });
      })
      // Every rejection reason (400 malformed key, 404 unresolvable/expired/
      // revoked/no-safeProjection, network failure) renders the identical
      // state — §8.2/§11 non-disclosure applies to this page exactly as it
      // applies to the API layer and to Core's own /link/:key route.
      .catch(() => { if (active) setState({ status: 'unavailable', sheet: null }); });
    return () => { active = false; };
  }, [key]);

  if (state.status === 'loading') return <LoadingView />;
  if (state.status === 'ready') return <SheetView sheet={state.sheet} />;
  return <UnavailableView />;
}
